import React, { useEffect, useState } from "react";
import ContentHeading from "../../../components/layout/content/ContentHeading";
import InputTextField from "../../../components/input/inputTextField";
import TableCustom from "../../../components/table/tableCustom";
import TableRowCommon from "../../../components/table/TableRowCommon";
import { useDispatch } from "react-redux";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { SearchOutlined } from "@mui/icons-material";
import { debouncedApiCall } from "../../../utils/app.utils";
import { CourseAnnualDetailAPI } from "../../../api/course-annual-detail/course-annual-detail.api";
import { dateToView } from "../../../utils/date.utils";
import { icons } from "../../../assets/icons/icons";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../components/alert/swal";
import { setLoading, unsetLoading } from "../../../store/loading/loading.slice";
import {
  ButtonOutlined,
  ButtonPrimary,
} from "../../../components/button/ButtonCustoms";
import ModalCustom from "../../../components/modal/ModalCustom";
import { RoomMeetingManagementApi } from "../../../api/room-meeting-management/room-meeting-management.api";
import SwitchCustom from "../../../components/switch/SwitchCustom";
import { routeName } from "../../../routes/routeName";
import { useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

type Props = {};

const BookingRoomManagementContainer: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));

  const [data, setData] = useState<any[]>([]);

  const [search, setSearch] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortType, setSortType] = useState<string>("ASC");
  const handleRequestSort = (sortBy: string, sortType: string) => {
    setSortBy(sortBy);
    setSortType(sortType);
  };

  const getData = async () => {
    let query: any = {
      page: page,
      limit: pageLimit,
      sortBy: sortBy,
      sortType: sortType,
      keyword: search ?? "",
    };
    dispatch(setLoading());
    await RoomMeetingManagementApi.getRoomMeetingList(query).then((res) => {
      if (res.status < 300) {
        const data = res.data;
        setData(data?.data?.items);
        setTotalItems(res.data?.data?.meta?.totalItems || 0);
      } else {
        setData([]);
      }
    }).catch((err) => {
      console.log(err)
    }).finally(() => {
      dispatch(unsetLoading());
    })
  };


  const handleUpdateStatusPublic = async (id_room_meeting: number) => {
    await RoomMeetingManagementApi.updateStatusPublic(id_room_meeting).then((res) => {
      if (res.status < 300) {
        getData();
      }
    }).catch((err) => {
      console.log(err)
      alertError(t("อัพเดตสถานะไม่สำเร็จ"))
    })
  }

  const handleDelete = async (id_room_meeting: number) => {
    if (id_room_meeting) {
      alertActive(
        t("ลบจัดการห้องเรียน/ห้องประชุม"),
        t("ท่านต้องการลบจัดการห้องเรียน/ห้องประชุม ใช่หรือไม่"),
        undefined,
        undefined,
        async () => {
          dispatch(setLoading());
          await RoomMeetingManagementApi.deleteRoomMeeting(+id_room_meeting)
            .then((res) => {
              if (res.status < 300) {
                alertSuccess(
                  t("ลบจัดการห้องเรียน/ห้องประชุม เรียบร้อย"),
                  undefined,
                  undefined,
                  () => {
                    getData();
                  }
                );
              }
            })
            .catch(() => {
              alertError(t("ลบจัดการห้องเรียน/ห้องประชุม ไม่สำเร็จ"));
            })
            .finally(() => {
              dispatch(unsetLoading());
            });
        }
      );
    }
    return;
  };

  const headCells = [
    {
      id: "no",
      width: 50,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ลำดับ"),
    },
    {
      id: "ประเภทห้อง", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ประเภทห้อง"),
    },
    {
      id: "ชื่อห้อง", // i18n-ignore: column id (not displayed)
      width: 150,
      sortable: false,
      disablePadding: false,
      align: "start",
      label: t("ชื่อห้อง"),
    },
    {
      id: "สถานะ", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("สถานะ"),
    },
    {
      id: "ผู้ที่สร้าง/ปรับปรุงล่าสุด", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ผู้ที่สร้าง/ปรับปรุงล่าสุด"),
    },
    {
      id: "วันที่สร้าง/ปรับปรุงล่าสุด", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("วันที่สร้าง/ปรับปรุงล่าสุด"),
    },
    {
      id: "action",
      width: 75,
      sortable: false,
      disablePadding: false,
      class: matches && data?.length == 0,
      align: "start",
      label: "",
    },
  ];

  const renderData = (objData: any, no: any) => {
    no = page * pageLimit - pageLimit + no + 1;
    const {
      id_room_meeting,
      room_meeting_type,
      room_meeting_name,
      status,
      modified_by,
      modified_date,
    } = objData;

    const objRenderData = {
      key: id_room_meeting,
      id: no,
      obj: objData,
      columns: [
        {
          option: "TEXT",
          align: "start",
          label: no ?? "-",
        },
        {
          option: "TEXT",
          align: "start",
          label: room_meeting_type ? t(room_meeting_type) : "-",
        },
        {
          option: "TEXT",
          align: "start",
          label: room_meeting_name ?? "-",
        },

        {
          option: "TEXT",
          align: "center",
          label: (
            <div className="w-full flex justify-center ">
              <SwitchCustom
                checked={status === "active" ? true : false}
                onChange={() => {
                  handleUpdateStatusPublic(id_room_meeting)
                  // handleSubmit(id_leave_type, value ? "active" : "inactive");
                }}
              />
            </div>
          ),
        },
        {
          option: "TEXT",
          align: "start",
          label: modified_by ?? "-",
        },
        {
          option: "TEXT",
          align: "start",
          label: modified_date ? dateToView(modified_date) : "",
        },
        {
          option: "COMPONENT",
          align: "center",
          class: matches,
          component: (
            <div
              style={{
                display: "flex",
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              <img
                onClick={() => {
                  navigate(
                    routeName.cms.BookingRoomManagement +
                    `/${id_room_meeting}/update?mode=VIEW`
                  )
                }}
                src={icons.eye}
                alt="eye"
                style={{ cursor: "pointer" }}
              />
              <img
                onClick={() => {
                  navigate(
                    routeName.cms.BookingRoomManagement +
                    `/${id_room_meeting}/update?mode=UPDATE`
                  );
                }}
                src={icons.edit}
                alt="edit"
                style={{ cursor: "pointer", margin: "0px 12px" }}
              />
              <img
                src={icons.bag_cross}
                alt="bag_cross"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  handleDelete(id_room_meeting);
                }}
              />
            </div>
          ),
        },
      ],
    };
    return <TableRowCommon {...objRenderData} />;
  };

  useEffect(() => {
    getData();
  }, [page, pageLimit, sortBy, sortType, search]);

  return (
    <div>
      <ContentHeading
        title={t("จัดการห้องเรียน/ห้องประชุม")}
        detail={t("กรุณาเลือกรายการที่ท่านต้องการ")}
      />

      <div className="w-full grid grid-cols-12 gap-4 mt-[40px]">
        <article className="w-full col-span-12 lg:col-span-3 md:col-span-3 sm:col-span-3">
          <InputTextField
            startAdornment={<SearchOutlined />}
            placeholder={t("ค้นหา ชื่อห้องเรียน/ห้องประชุม")}
            onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
              debouncedApiCall({
                searchItem: e.target.value,
                setItem: setSearch,
              })
            }
          />
        </article>

        <article className="w-full col-span-12 lg:col-span-9 md:col-span-9 sm:col-span-9 flex justify-end">
          <ButtonPrimary
            onClick={() => {
              navigate(routeName.cms.BookingRoomManagementCreate);
            }}
            mode="add"
            buttonName={t("ทำเรื่องขอจอง")}
          />
        </article>
      </div>

      <section className="w-full mt-10">
        <TableCustom
          tableMinWidth={1440}
          tableFixedWidth
          customScroll
          page={page}
          pageLimit={pageLimit}
          sortBy={sortBy}
          sortType={sortType}
          headCells={headCells}
          rowCount={totalItems}
          rowsData={
            data &&
            data.map((data: any, i: any) => {
              return renderData(data, i);
            })
          }
          onSort={handleRequestSort}
          setPageLimit={(value: number) => setPageLimit(value)}
          setPage={(value: number) => setPage(value)}
        />
      </section>
    </div>
  );
};

export default BookingRoomManagementContainer;
